import React, { useEffect, useState } from "react";
import {
  Button,
  Table,
  Tag,
  Popconfirm,
  Modal,
  message,
  Select,
  notification,
  Skeleton,
} from "antd";
import { useLoading } from "../../context/loadingContext";
import GetSettleTransaction from "../../functions/merchant/getSettleTransactions";
import moneyFormat from "../../functions/utils/moneyFormat";
import dayjs from "dayjs";
import ForceSettle from "../../functions/merchant/forceSettle";
import GetIncomeTransaction from "../../functions/merchant/getIncomeTransaction";
import SettleTransaction from "../../functions/merchant/settleTransaction";
import { TransactionOutlined } from "@ant-design/icons";
import { bg_button, bg_shadow, bg_text } from "constant/css";
import CheckPermission from "functions/utils/checkPermission";
import BankSelectModal from "./bankSelectModal";
import UpdateTransactionBank from "functions/merchant/updateTransactionBank";
import { parklyLogin } from "functions/parkly/login";
import { parklyPaymentSummary } from "functions/parkly/paymentSummary";
import { getMerchantId } from "service/storage";
import GetSettleTransactionSummary from "../../functions/merchant/getSettleTransactionSummary";
import GetSettleUniqueAccounts from "../../functions/merchant/getSettleUniqueAccounts";
import { RetryFailedIncomes } from "../../functions/admin/feeShareRule";
import { ReloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";

const ColTitle = ({ text }) => (
  <span className="text-xs font-bold text-gray-700">{text}</span>
);

const StatusTag = ({ status }) =>
  status === "SETTLED" ? (
    <Tag color="cyan" className="text-xs">Шилжүүлсэн</Tag>
  ) : (
    <Tag color="default" className="text-xs">Хүлээгдэж байна</Tag>
  );

const ParklyDetailModal = ({ open, onClose, data }) => (
  <Modal
    title="Parkly дэлгэрэнгүй"
    open={open}
    onCancel={onClose}
    footer={null}
    width={460}
  >
    <div className="space-y-3">
      <div className="rounded-xl p-4 text-white bg-gradient-to-r from-sky-500 to-cyan-500 shadow-sm">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-sky-100">spay_grand_total</span>
          <img src="/merchant/parkly.png" alt="Parkly" className="h-5" />
        </div>
        <div className="text-2xl font-bold mt-1 tracking-wide">
          {moneyFormat(data?.spay_grand_total || 0)}
        </div>
        <div className="text-xs text-sky-100 mt-1">
          Transactions: {data?.total_transactions || 0}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {[
          { label: "POS total", value: data?.pos_total },
          { label: "Smartpay total", value: data?.smartpay_total },
          { label: "Cash total", value: data?.cash_total },
          { label: "Grand total", value: data?.grand_total },
        ].map(({ label, value }) => (
          <div key={label} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className="text-sm font-semibold text-slate-800 mt-1">{moneyFormat(value || 0)}</div>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-cyan-100 bg-cyan-50 p-3 flex items-center justify-between">
        <span className="text-xs font-medium text-cyan-700">Status</span>
        <span className="text-xs font-semibold text-cyan-800">
          {data?.grand_total ? "Summary data loaded" : "No summary data"}
        </span>
      </div>
    </div>
  </Modal>
);

const SettleTransactions = ({ open, close, refresh, defaultMerchantId = null }) => {
  const merchantSettleId = open;
  const { showLoading, hideLoading } = useLoading();
  const merchantId = defaultMerchantId || getMerchantId();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [incomeData, setIncomeData] = useState([]);
  const [uniqueAccounts, setUniqueAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [pickBankTran, setPickBankTran] = useState(null);
  const [parklyData, setParklyData] = useState(null);
  const [filteredParklyData, setFilteredParklyData] = useState(null);
  const [loadingParkly, setLoadingParkly] = useState(false);
  const [parklyDetailOpen, setParklyDetailOpen] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [incomePagination, setIncomePagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [summary, setSummary] = useState(null);
  const [retryingIncome, setRetryingIncome] = useState(false);

  useEffect(() => {
    if (merchantSettleId) {
      setSelectedAccount(null);
      setSelectedStatus(null);
      fetchUniqueAccounts();
      fetchTransactions(1, 1, null, null);
    }
    // eslint-disable-next-line
  }, [merchantSettleId]);

  const buildActionCell = (e) => (
    <CheckPermission allow={3}>
      <Popconfirm
        title="bitgii dar"
        disabled={e.status === "SETTLED"}
        okText="forceSettle"
        onConfirm={() => settleTransaction(e.id)}
      >
        <Button
          disabled={e.status === "SETTLED"}
          icon={<TransactionOutlined style={{ color: "#fff" }} />}
          className={`${bg_button} hover:bg-blue-700 border-none`}
        />
      </Popconfirm>
    </CheckPermission>
  );

  const fetchUniqueAccounts = async () => {
    setLoadingAccounts(true);
    const response = await GetSettleUniqueAccounts(merchantId, merchantSettleId);
    if (response?.success) setUniqueAccounts(response.data);
    setLoadingAccounts(false);
  };

  const fetchTransactions = async (
    page = 1,
    incomePage = 1,
    accountNumber = selectedAccount,
    status = selectedStatus
  ) => {
    setLoading(true);
    const [response, incomeResponse, summaryResponse] = await Promise.all([
      GetSettleTransaction(merchantId, merchantSettleId, page, 20, accountNumber, status),
      GetIncomeTransaction(merchantId, merchantSettleId, incomePage, 20),
      GetSettleTransactionSummary(merchantId, merchantSettleId, accountNumber, status),
    ]);

    if (summaryResponse?.success) setSummary(summaryResponse.data);

    if (incomeResponse?.success) {
      setIncomeData(incomeResponse.data);
      setIncomePagination((prev) => ({
        ...prev,
        current: incomePage,
        total: incomeResponse.pagination?.total || 0,
      }));
    }

    if (response?.success) {
      const mapped = response.data.map((e) => {
        const nameFromCache = uniqueAccounts.find((a) => a.accountNumber === e.accountNumber)?.toAccountName;
        return { ...e, toAccountName: e.toAccountName || nameFromCache || "Байгууллага олдсонгүй", action: buildActionCell(e) };
      });
      setData(mapped);
      setPagination((prev) => ({
        ...prev,
        current: page,
        total: response.pagination?.total || 0,
      }));

      if (!accountNumber && Number(merchantId) === 1) {
        fetchParklyData(response.data);
      }
    }

    setLoading(false);
  };

  const fetchParklyData = async (transactions) => {
    setLoadingParkly(true);
    const uniqueAccountNumbers = [...new Set(transactions.map((e) => e.accountNumber))];
    const pData = {
      total: 0,
      byAccounts: {},
      detailsByAccount: {},
      cash_total: 0,
      grand_total: 0,
      pos_total: 0,
      smartpay_total: 0,
      spay_grand_total: 0,
      total_transactions: 0,
    };
    const parklyDate = dayjs(transactions[0].createdAt).format("YYYY-MM-DD");
    for (const account of uniqueAccountNumbers) {
      const d = await fetchParklyAmount(account, parklyDate);
      pData.detailsByAccount[account] = d || {};
      pData.cash_total += Number(d?.cash_total || 0);
      pData.grand_total += Number(d?.grand_total || 0);
      pData.pos_total += Number(d?.pos_total || 0);
      pData.smartpay_total += Number(d?.smartpay_total || 0);
      pData.spay_grand_total += Number(d?.spay_grand_total || 0);
      pData.total_transactions += Number(d?.total_transactions || 0);
      const totalXpay = Number(d?.spay_grand_total || 0);
      if (totalXpay) {
        pData.byAccounts[account] = totalXpay;
        pData.total += totalXpay;
      }
    }
    setParklyData(pData);
    setFilteredParklyData(pData);
    setLoadingParkly(false);
  };

  const fetchParklyAmount = async (account, date) => {
    try {
      const token = await parklyLogin();
      if (!token) {
        notification.error({ message: "Parkly token-ийг авахад алдаа гарлаа" });
        return null;
      }
      const res = await parklyPaymentSummary(token, account, date);
      return res.data;
    } catch (e) {
      console.log("fetchParklyAmount error:", e);
    }
  };

  const filterAccountName = (accountNumber) => {
    if (!accountNumber) return;
    setSelectedAccount(accountNumber);
    fetchTransactions(1, incomePagination.current, accountNumber, selectedStatus);
    if (parklyData) {
      setFilteredParklyData({
        ...parklyData.detailsByAccount?.[accountNumber],
        total: parklyData.byAccounts[accountNumber] || 0,
      });
    }
  };

  const clearAccountFilter = () => {
    setSelectedAccount(null);
    setFilteredParklyData(parklyData);
    fetchTransactions(1, incomePagination.current, null, selectedStatus);
  };

  const filterByStatus = (status) => {
    setSelectedStatus(status || null);
    fetchTransactions(1, incomePagination.current, selectedAccount, status || null);
  };

  const clearStatusFilter = () => {
    setSelectedStatus(null);
    fetchTransactions(1, incomePagination.current, selectedAccount, null);
  };

  const settleTransaction = async (tranId) => {
    showLoading();
    const response = await SettleTransaction(tranId);
    if (response?.success) fetchTransactions(pagination.current, incomePagination.current);
    hideLoading();
  };

  const exportExcel = () => {
    const rows = data.map((e) => ({
      "ID": e.id,
      "Нийт дүн": e.amount,
      "Шилжүүлэх данс": e.fromAccountNumber,
      "Хүлээн авах данс": e.accountNumber,
      "Хүлээн авагч": e.toAccountName,
      "Төлөв": e.status === "SETTLED" ? "Шилжүүлсэн" : "Хүлээгдэж байна",
      "Өндөрлөсөн огноо": e.settledAt ? dayjs(e.settledAt).format("YYYY-MM-DD HH:mm") : "",
      "Ш/дугаар": e.journalNo,
      "Мерчант %": e.merchantFeePercent,
      "Мерчант дүн": e.merchantFee,
      "xPay %": e.xpayFeePercent,
      "xPay дүн": e.xpayFee,
      "Процэсс %": e.processingFeePercent,
      "Процэсс дүн": e.processingFee,
      "Эцсийн дүн": e.afterFeeAmount,
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Гүйлгээ");
    XLSX.writeFile(workbook, `settle_${selectedAccount}_${dayjs().format("YYYY-MM-DD")}.xlsx`);
  };

  const forceSettle = async () => {
    showLoading();
    try {
      const response = await ForceSettle(merchantId, merchantSettleId);
      const result = response?.data;
      if (result?.success) {
        message.success(result.message || "Амжилттай өндөрлөө");
        refresh();
        fetchTransactions(1, 1);
      } else {
        message.error(result?.message || response?.message || "Өндөрлөхөд алдаа гарлаа");
      }
    } catch (e) {
      message.error(String(e?.message || e));
    } finally {
      hideLoading();
    }
  };

  const retryFailedIncome = async () => {
    setRetryingIncome(true);
    showLoading();
    try {
      const response = await RetryFailedIncomes(merchantSettleId);
      if (response && response.success) {
        const succeeded = response.succeeded || 0;
        const failed = response.failed || 0;
        const skipped = response.skipped || 0;
        notification.success({
          message: "Дахин шилжүүлэг дууссан",
          description: `Амжилттай: ${succeeded}, Алдаатай: ${failed}, Алгассан: ${skipped}`,
        });
      } else {
        notification.warning({
          message: "Дахин шилжүүлэг хэсэгчилсэн",
          description: response?.message || "Дэлгэрэнгүйг лог хараарай",
        });
      }
      fetchTransactions(pagination.current, incomePagination.current);
    } catch (e) {
      notification.error({
        message: "Алдаа гарлаа",
        description: String(e?.message || e),
      });
    } finally {
      setRetryingIncome(false);
      hideLoading();
    }
  };

  const saveTransaction = async (bankCode) => {
    showLoading();
    const response = await UpdateTransactionBank(pickBankTran.id, bankCode);
    hideLoading();
    if (response?.success) {
      message.success("Амжилттай солигдлоо");
      fetchTransactions(pagination.current, incomePagination.current);
      setPickBankTran(null);
    } else {
      message.error("Алдаа гарлаа");
    }
  };

  const columns = [
    { title: "#", dataIndex: "id", key: "id", fixed: "left", width: 50 },
    {
      title: <ColTitle text="Нийт дүн" />,
      dataIndex: "amount",
      key: "amount",
      width: 110,
      render: (e) => moneyFormat(e),
    },
    {
      title: <ColTitle text="Шилжүүлэх" />,
      dataIndex: "fromAccountNumber",
      key: "fromAccountNumber",
      width: 130,
    },
    {
      title: <ColTitle text="Хүлээн авах" />,
      dataIndex: "accountNumber",
      key: "accountNumber",
      width: 130,
    },
    {
      title: (
        <div className="min-w-[120px]">
          <ColTitle text="Хүлээн авагч" />
          <Select
            onChange={filterAccountName}
            className="w-full mt-1"
            size="small"
            placeholder="Бүгд"
            allowClear
            loading={loadingAccounts}
            onClear={clearAccountFilter}
            value={selectedAccount}
          >
            {uniqueAccounts.map((a) => (
              <Select.Option key={a.accountNumber} value={a.accountNumber}>{a.toAccountName}</Select.Option>
            ))}
          </Select>
        </div>
      ),
      dataIndex: "toAccountName",
      key: "toAccountName",
      width: 150,
      render: (e, r) =>
        e === "Байгууллага олдсонгүй" ? (
          <Tag color="red" onClick={() => setPickBankTran(r)} className="cursor-pointer">
            Банк сонгоx
          </Tag>
        ) : (
          <span className="text-gray-700 text-xs">{e}</span>
        ),
    },
    {
      title: (
        <div className="min-w-[100px]">
          <ColTitle text="Төлөв" />
          <Select
            onChange={filterByStatus}
            className="w-full mt-1"
            size="small"
            placeholder="Бүгд"
            allowClear
            onClear={clearStatusFilter}
            value={selectedStatus}
          >
            <Select.Option value="SETTLED">Шилжүүлсэн</Select.Option>
            <Select.Option value="PENDING">Хүлээгдэж байна</Select.Option>
          </Select>
        </div>
      ),
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (e) => <StatusTag status={e} />,
    },
    {
      title: <ColTitle text="Өндөрлөсөн огноо" />,
      dataIndex: "settledAt",
      key: "settledAt",
      width: 130,
      render: (e) => (
        <span className="text-xs">{e ? dayjs(e).format("YYYY-MM-DD HH:mm") : "—"}</span>
      ),
    },
    { title: <ColTitle text="Ш/дугаар" />, dataIndex: "journalNo", key: "journalNo", width: 110 },
    { title: <ColTitle text="Мерчант %" />, dataIndex: "merchantFeePercent", key: "merchantFeePercent", width: 90 },
    { title: <ColTitle text="Мерчант дүн" />, dataIndex: "merchantFee", key: "merchantFee", width: 100 },
    { title: <ColTitle text="xPay %" />, dataIndex: "xpayFeePercent", key: "xpayFeePercent", width: 80 },
    {
      title: <ColTitle text="xPay дүн" />,
      dataIndex: "xpayFee",
      key: "xpayFee",
      width: 100,
      render: (e) => <span className="text-orange-500">{moneyFormat(e)}</span>,
    },
    { title: <ColTitle text="Процэсс %" />, dataIndex: "processingFeePercent", key: "processingFeePercent", width: 90 },
    {
      title: <ColTitle text="Процэсс дүн" />,
      dataIndex: "processingFee",
      key: "processingFee",
      width: 105,
      render: (e) => <span className="text-orange-700">{moneyFormat(e)}</span>,
    },
    {
      title: <ColTitle text="Эцсийн дүн" />,
      dataIndex: "afterFeeAmount",
      key: "afterFeeAmount",
      width: 105,
      render: (e) => <span className={`font-semibold ${bg_text}`}>{moneyFormat(e)}</span>,
    },
    { title: "", dataIndex: "action", key: "action", width: 60, fixed: "right" },
  ];

  const incomeColumns = [
    { title: "#", dataIndex: "id", key: "id", fixed: "left", width: 50 },
    { title: "Нийт дүн", dataIndex: "amount", key: "amount", width: 120, render: (e) => moneyFormat(e) },
    { title: "Аль данснаас", dataIndex: "fromAccountNumber", key: "fromAccountNumber", width: 140 },
    { title: "Дансруу", dataIndex: "accountNumber", key: "accountNumber", width: 130 },
    {
      title: "Хүлээн авагч",
      dataIndex: "toAccountName",
      key: "toAccountName",
      render: (e) => <span className="text-gray-700 text-xs">{e}</span>,
    },
    {
      title: "Төлөв",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (e) => <StatusTag status={e} />,
    },
    {
      title: "Шилжүүлсэн огноо",
      dataIndex: "settledAt",
      key: "settledAt",
      width: 150,
      render: (e) => (e ? dayjs(e).format("YYYY-MM-DD HH:mm") : "—"),
    },
    { title: "Ш/дугаар", dataIndex: "journalNo", key: "journalNo", width: 110 },
  ];

  const tableSummary = () => {
    const s = summary || {};
    return (
      <Table.Summary fixed>
        <Table.Summary.Row className="bg-gray-50 font-medium">
          <Table.Summary.Cell index={0}>{s.total ?? "—"}</Table.Summary.Cell>
          <Table.Summary.Cell index={1}>
            <span className={`font-semibold ${bg_text}`}>{moneyFormat(s.totalAmount)}</span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={2}>
            {loadingParkly ? (
              <div className="w-20 h-5 bg-gray-200 animate-pulse rounded" />
            ) : (
              <div
                className="border rounded-lg px-2 py-1 cursor-pointer inline-flex items-center gap-1"
                onClick={() => setParklyDetailOpen(true)}
              >
                <img src="/merchant/parkly.png" alt="Parkly" className="h-4" />
                <span className="font-semibold text-blue-600 text-xs">
                  {filteredParklyData ? moneyFormat(filteredParklyData.total) : "0"}
                </span>
              </div>
            )}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={3} />
          <Table.Summary.Cell index={4}>
            <div className="flex flex-col gap-0.5 text-xs">
              <span className="font-semibold text-[#32B7C2]">{moneyFormat(s.settledAmount)}</span>
              <span className="font-semibold text-red-500">{moneyFormat(s.pendingAmount)}</span>
            </div>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={5} />
          <Table.Summary.Cell index={6} />
          <Table.Summary.Cell index={7} />
          <Table.Summary.Cell index={8} />
          <Table.Summary.Cell index={9} className={bg_text}>{moneyFormat(s.totalMerchantFee)}</Table.Summary.Cell>
          <Table.Summary.Cell index={10} />
          <Table.Summary.Cell index={11} className="text-orange-500">{moneyFormat(s.totalXpayFee)}</Table.Summary.Cell>
          <Table.Summary.Cell index={12} />
          <Table.Summary.Cell index={13} className="text-orange-700">{moneyFormat(s.totalProcessingFee)}</Table.Summary.Cell>
          <Table.Summary.Cell index={14} className={`${bg_text} font-bold`}>{moneyFormat(s.totalAfterFeeAmount)}</Table.Summary.Cell>
          <Table.Summary.Cell index={15} />
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  return (
    <>
      <Modal
        title={<span className={`text-xl font-bold ${bg_text}`}>Мерчант гүйлгээ  </span>}
        open={open}
        width="92vw"
        style={{ top: 16, maxWidth: 1920 }}
        onCancel={close}
        footer={
          <CheckPermission allow={3}>
            <div className="flex justify-end">
              <Popconfirm
                title="Эрсдэлтэйгээр өндөрлөх үү?"
                okText="forceSettle"
                onConfirm={forceSettle}
              >
                <Button className={`${bg_button} ${bg_shadow} text-white font-semibold min-w-[120px]`}>
                  Өндөрлүүлэх
                </Button>
              </Popconfirm>
            </div>
          </CheckPermission>
        }
        styles={{ body: { padding: "12px 16px" } }}
      >
        {loading ? (
          <div className="space-y-3 py-4">
            <Skeleton active paragraph={{ rows: 6 }} />
            <Skeleton active paragraph={{ rows: 3 }} />
          </div>
        ) : (
          <div className="space-y-5">
            <div>
              {selectedAccount && (
                <div className="flex justify-end mb-1">
                  <Button
                    onClick={exportExcel}
                    className={`${bg_button} ${bg_shadow} text-white font-semibold border-none`}
                    size="small"
                  >
                    Excel татах
                  </Button>
                </div>
              )}
              <Table
                columns={columns}
                rowKey={(record) => record.id}
                dataSource={data}
                scroll={{ x: "max-content", y: 340 }}
                size="small"
                pagination={
                  selectedAccount
                    ? false
                    : {
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: false,
                        showTotal: (total) => `Нийт ${total} гүйлгээ`,
                        onChange: (page) =>
                          fetchTransactions(page, incomePagination.current, selectedAccount, selectedStatus),
                      }
                }
                summary={tableSummary}
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <h3 className={`text-base font-bold ${bg_text}`}>Орлогын гүйлгээ</h3>
                {incomeData.some((e) => e.status !== "SETTLED") && (
                  <CheckPermission allow={3}>
                    <Popconfirm
                      title="FAILED орлогын гүйлгээг дахин шилжүүлэх үү?"
                      description="feeShareRule-ийн засагдсан дансны мэдээллийг ашиглана. SETTLED болсон хүрэхгүй."
                      okText="Дахин шилжүүлэх"
                      cancelText="Болих"
                      onConfirm={retryFailedIncome}
                    >
                      <Button
                        danger
                        icon={<ReloadOutlined />}
                        size="small"
                        loading={retryingIncome}
                      >
                        FAILED-ийг дахин шилжүүлэх
                      </Button>
                    </Popconfirm>
                  </CheckPermission>
                )}
              </div>
              <Table
                columns={incomeColumns}
                rowKey={(record) => record.id}
                dataSource={incomeData}
                scroll={{ x: "max-content", y: 120 }}
                size="small"
                pagination={{
                  current: incomePagination.current,
                  pageSize: incomePagination.pageSize,
                  total: incomePagination.total,
                  showSizeChanger: false,
                  showTotal: (total) => `Нийт ${total}`,
                  onChange: (page) => fetchTransactions(pagination.current, page),
                }}
              />
            </div>
          </div>
        )}
      </Modal>

      {pickBankTran && (
        <BankSelectModal
          open={pickBankTran}
          value={pickBankTran?.bankCode}
          save={saveTransaction}
          close={() => setPickBankTran(null)}
        />
      )}

      <ParklyDetailModal
        open={parklyDetailOpen}
        onClose={() => setParklyDetailOpen(false)}
        data={filteredParklyData}
      />
    </>
  );
};

export default SettleTransactions;
